import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const EASE = [0.21, 0.47, 0.32, 0.98];
const vp = { once: true, amount: 0.4 };

/* ---- small animated mini-visuals (play on scroll-in) ---- */

const Shell = ({ children }) => (
  <div className="h-16 mb-4 flex items-end justify-center gap-1.5">{children}</div>
);

// Rate-safe: a safe "50/50" meter that fills part-way (never maxes = stays safe)
const VisRateSafe = ({ darkMode }) => (
  <div className="h-16 mb-4 flex flex-col justify-center">
    <div className={`w-full h-2.5 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
      <motion.div
        initial={{ width: 0 }} whileInView={{ width: '64%' }} viewport={vp}
        transition={{ duration: 1.1, ease: EASE }}
        className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-cyan-500"
      />
    </div>
    <div className="flex justify-between mt-1.5 text-[10px] font-medium">
      <span className="text-emerald-400">32 sent</span>
      <span className={darkMode ? 'text-slate-500' : 'text-gray-400'}>50 / day cap</span>
    </div>
  </div>
);

// A/B: two bars, variant B grows taller (winner)
const VisAB = ({ darkMode }) => (
  <Shell>
    {[{ h: 38, label: 'A', win: false }, { h: 60, label: 'B', win: true }].map((b) => (
      <div key={b.label} className="flex flex-col items-center gap-1">
        <motion.div
          initial={{ height: 0 }} whileInView={{ height: b.h }} viewport={vp}
          transition={{ duration: 0.8, ease: EASE, delay: b.win ? 0.15 : 0 }}
          className={`w-7 rounded-t ${b.win ? 'bg-gradient-to-t from-cyan-500 to-blue-500' : (darkMode ? 'bg-slate-600' : 'bg-gray-300')}`}
        />
        <span className={`text-[10px] font-semibold ${b.win ? 'text-cyan-400' : (darkMode ? 'text-slate-500' : 'text-gray-400')}`}>{b.label}</span>
      </div>
    ))}
  </Shell>
);

// Follow-ups: chat bubbles appear in sequence
const VisFollowups = ({ darkMode }) => (
  <div className="h-16 mb-4 flex items-center justify-center gap-2">
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, scale: 0.5, y: 6 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={vp}
        transition={{ delay: i * 0.25, type: 'spring', stiffness: 380, damping: 18 }}
        className={`h-8 rounded-2xl rounded-bl-sm ${i === 0 ? 'w-16' : i === 1 ? 'w-12' : 'w-14'} ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center px-2`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mr-1" />
        <span className={`flex-1 h-1 rounded ${darkMode ? 'bg-slate-500' : 'bg-gray-400'}`} />
      </motion.div>
    ))}
  </div>
);

// Multi-account: avatar circles fan in
const VisMulti = ({ darkMode }) => (
  <div className="h-16 mb-4 flex items-center justify-center">
    <div className="flex">
      {['M', 'T', 'S', '+'].map((t, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -14, scale: 0.6 }}
          whileInView={{ opacity: 1, x: 0, scale: 1 }}
          viewport={vp}
          transition={{ delay: i * 0.12, type: 'spring', stiffness: 320, damping: 20 }}
          style={{ marginLeft: i === 0 ? 0 : -10, zIndex: 10 - i }}
          className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-white ring-2 ${darkMode ? 'ring-slate-800' : 'ring-white'} ${i === 3 ? (darkMode ? 'bg-slate-600' : 'bg-gray-400') : 'bg-gradient-to-r from-cyan-500 to-blue-600'}`}
        >
          {t}
        </motion.div>
      ))}
    </div>
  </div>
);

// Unified inbox: message rows stack in
const VisInbox = ({ darkMode }) => (
  <div className="h-16 mb-4 flex flex-col justify-center items-center gap-1.5">
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, x: -16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={vp}
        transition={{ delay: i * 0.15, duration: 0.4, ease: EASE }}
        className="flex items-center gap-2"
      >
        <span className="w-5 h-5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 shrink-0" />
        <span className={`h-2 rounded ${i === 0 ? 'w-28' : i === 1 ? 'w-20' : 'w-24'} ${darkMode ? 'bg-slate-600' : 'bg-gray-300'}`} />
      </motion.div>
    ))}
  </div>
);

// Analytics: sparkline draws upward (useInView = reliable trigger, like the steps section)
const VisAnalytics = ({ darkMode }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <div ref={ref} className="h-16 mb-4 flex items-center justify-center">
      <svg viewBox="0 0 120 48" className="w-44 max-w-full h-12" fill="none">
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
        </defs>
        <motion.path
          d="M2 42 L26 30 L50 34 L74 18 L98 22 L118 6"
          stroke="url(#sparkGrad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: inView ? 1 : 0 }}
          transition={{ duration: 1.2, ease: EASE }}
        />
      </svg>
    </div>
  );
};

const FEATURES = [
  { Vis: VisRateSafe, title: 'Human-like & rate-safe', desc: 'Randomized delays, typing simulation, and daily send caps keep your accounts looking human and protected.' },
  { Vis: VisAB, title: 'A/B tested openers', desc: 'Run two opener variants per campaign and let real reply data pick the winner automatically.' },
  { Vis: VisFollowups, title: 'Automated follow-ups', desc: 'Sequenced follow-ups fire on their own schedule until a prospect replies — no manual chasing.' },
  { Vis: VisMulti, title: 'Multi-account', desc: 'Run many Instagram accounts in parallel from one dashboard, each with its own limits and sessions.' },
  { Vis: VisInbox, title: 'Unified inbox', desc: 'Every reply lands in one place, tied to the campaign and lead that started the conversation.' },
  { Vis: VisAnalytics, title: 'Reply analytics', desc: 'Per-campaign reply rates and progress so you double down on what actually books meetings.' },
];

const Features = ({ darkMode }) => {
  return (
    <section className={`py-16 lg:py-24 ${darkMode ? 'bg-slate-900/40' : 'bg-white/50'}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Everything you need to run outreach
          </h2>
          <p className={`mt-3 text-lg max-w-2xl mx-auto ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
            Built for volume, tuned for safety — so you can scale DMs without burning accounts.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {FEATURES.map((f, i) => {
            const Vis = f.Vis;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.1, ease: EASE }}
                whileHover={{ y: -6, transition: { type: 'spring', stiffness: 300, damping: 22 } }}
                className={`rounded-xl p-6 text-center border transition-shadow duration-300 hover:shadow-xl ${darkMode ? 'bg-slate-800/70 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}
              >
                <Vis darkMode={darkMode} />
                <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{f.title}</h3>
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
